/**
 * Lifecycle of a serialized device (one physical phone, identified by IMEI).
 *
 * The kosher provisioning chain is the heart of this: a device is not sellable
 * until it has been filtered/locked down by a technician.
 * Every transition is recorded in unit_status_history — the table is append-only.
 */
export const UNIT_STATUSES = [
    'received', // arrived from vendor, not yet touched
    'provisioning', // technician is installing the filter / locking the device
    'verified', // retired (t1356): kept so old history reads; nothing moves into it
    'sellable', // available to sell
    'reserved', // attached to an open order
    'sold', // handed to a customer
    'returned', // came back from a customer; needs re-provisioning before resale
    'rma', // sent back to vendor / manufacturer
    'scrapped', // dead, written off
    'rented', // out with a customer on a rental — stays shop-owned, expected back
    'lost', // never came back from a rental — out of stock, recoverable if it turns up
];
/** Statuses a unit can never leave. */
export const TERMINAL_UNIT_STATUSES = ['scrapped'];
/**
 * Legal transitions. Anything not listed here is rejected by the API with 409 —
 * the map is the single source of truth for the server, the UI, and the tests.
 */
export const UNIT_TRANSITIONS = {
    received: ['provisioning', 'rma', 'scrapped'],
    provisioning: ['sellable', 'received', 'rma', 'scrapped'],
    // Retired step (t1356): only here so a stray unit can still leave it.
    verified: ['sellable', 'provisioning', 'rma', 'scrapped'],
    sellable: ['reserved', 'provisioning', 'rma', 'scrapped', 'rented'],
    reserved: ['sold', 'sellable'], // released back if the order is cancelled
    sold: ['returned'],
    returned: ['provisioning', 'rma', 'scrapped'],
    rma: ['received', 'scrapped'], // vendor may send a replacement/repair back
    scrapped: [],
    // A returned rental goes straight back on the shelf by default; staff can
    // instead send it to provisioning if it needs a wipe or recheck first.
    rented: ['sellable', 'provisioning', 'lost'],
    // Not scrapped: a phone that turns up again can be re-provisioned.
    lost: ['provisioning', 'scrapped'],
};
export function canTransitionUnit(from, to) {
    return UNIT_TRANSITIONS[from].includes(to);
}
/** Ordered stages shown as tabs in the admin provisioning queue. */
export const PROVISIONING_PIPELINE = ['received', 'provisioning', 'sellable'];
/**
 * t1402: the forward provisioning moves where the technician must record what
 * they did (and, for a phone, whether a filter went on). Send-backs, returns,
 * RMA and the order/rental flows are deliberately not included.
 */
export function requiresWorkRecord(from, to) {
    return ((from === 'received' && to === 'provisioning') ||
        ((from === 'provisioning' || from === 'verified') && to === 'sellable'));
}
//# sourceMappingURL=unit-states.js.map